import { Component, HostListener, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef, NgZone, AfterViewInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GlobalService } from '../../services/global.service';
import { LoadingService } from '../loading/loading.service';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  time: string;
  isTyping?: boolean;
}

interface WsHistoryEntry {
  role: string;
  content: string;
  end: boolean;
  stream_id: number | string;
  created_at: string;
}

@Component({
  selector: 'app-chat-bot',
  templateUrl: './chat-bot.component.html',
})

export class ChatBotComponent implements OnInit, OnDestroy, AfterViewChecked  {
  public safeUrl: SafeResourceUrl;
  public iframeHeight: string = '350px';

  // ── Config ────────────────────────────────────────────────────────────────
  private readonly BOT_ID = '7365';
  private DOMAIN = 'https://hvswebuat1.hvsfinancial.com'; // swap for prod
  private readonly WSS_URL = 'wss://ai.getbutton.io/wss';
  private readonly WELCOME =
    "Welcome to HVS Portal. I'm Vista, your virtual assistant for healthcare cost planning, Medicare, and more.";

  // ── State ─────────────────────────────────────────────────────────────────
  messages: ChatMessage[] = [];
  inputText = '';
  isConnected = false;
  isStreaming = false;
  showTyping = false;
  avatarError = false;

  // ── Private ───────────────────────────────────────────────────────────────
  private ws: WebSocket | null = null;
  private sessionUid = '';
  private currentStreamId: string | null = null;
  private wsHistory: WsHistoryEntry[] = [];
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private shouldScrollBottom = false;
  private assistantStarted = false;

  // Triggers when the user closes the tab, browser, or reloads the page
  /* @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    // Standard approach: setting returnValue triggers the browser's confirmation prompt
    //$event.preventDefault();
    //$event.returnValue = true; 
     $event.returnValue = 'Are you sure you want to leave?';
  } */

  @ViewChild('messagesContainer') private messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') private chatInput!: ElementRef<HTMLInputElement>;

  //@ViewChild('chatBotIframe') chatBotIframe!: ElementRef;

  //private iframeBlurListener = () => this.onIframeFocusLost();

  constructor(private sanitizer: DomSanitizer, private global: GlobalService, public loadingService: LoadingService,
    private cdr: ChangeDetectorRef, private ngZone: NgZone) {
      this.global.featChatBot = JSON.parse(localStorage.getItem('featChatBot'));
    }

  ngOnInit(): void {
    this.loadingService.isLoading(true);
    const externalUrl = this.global.botURL;
    this.DOMAIN = this.global.botURL;// ?? 'https://hvswebuat1.hvsfinancial.com';
    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(externalUrl);

    console.log('botURL:', this.global.botURL);         // ← is this populated?
    console.log('featChatBot:', this.global.featChatBot);

     // When user clicks INTO the iframe, the window loses focus
    //window.addEventListener('blur', this.iframeBlurListener);

    //connect to the WebSocket server to listen for incoming messages and send user messages
    this.connect();    
  }

  ngOnDestroy(): void {
    //window.removeEventListener('blur', this.iframeBlurListener);

    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.ws) {
      this.ws.onclose = null; // prevent reconnect on intentional destroy
      this.ws.close();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollBottom) {
      this.scrollBottom();
      this.shouldScrollBottom = false;
    }
  }

   // ── WebSocket ─────────────────────────────────────────────────────────────
  private connect(): void {
    console.log('connect() called, DOMAIN:', this.DOMAIN);
    this.sessionUid =
      this.DOMAIN.replace(/https?:\/\//, '') + '_' + this.uid(16);
     console.log('sessionUid:', this.sessionUid);
    console.log('WSS_URL:', this.WSS_URL);

    // Run WebSocket outside Angular zone to avoid triggering CD on every frame,
    // then re-enter zone only when we need to update the view.
    this.ngZone.runOutsideAngular(() => {
      console.log('creating WebSocket...');
      this.ws = new WebSocket(this.WSS_URL);
      console.log('WebSocket created, readyState:', this.ws.readyState);

      this.ws.onopen = () => {
        this.ngZone.run(() => {
          this.isConnected = true;
          this.messages = [
            { role: 'assistant', content: this.WELCOME, time: this.formatTime() },
          ];
          this.wsHistory = [
            {
              role: 'assistant',
              content: this.WELCOME,
              end: true,
              stream_id: 1,
              created_at: new Date().toISOString(),
            },
          ];
          this.shouldScrollBottom = true;
          this.cdr.detectChanges();
          //setTimeout(() => this.chatInput.nativeElement.focus());
          setTimeout(() => {
            if (this.chatInput) {
              this.chatInput.nativeElement.focus();
            }
          });
        });
      };

      this.ws.onclose = () => {
        this.ngZone.run(() => {
          this.isConnected = false;
          this.isStreaming = false;
          this.showTyping = false;
          this.cdr.detectChanges();
          this.reconnectTimer = setTimeout(() => this.connect(), 3000);
        });
      };

      this.ws.onerror = () => {
        this.ngZone.run(() => {
          this.isConnected = false;
          this.cdr.detectChanges();
        });
      };

      this.ws.onmessage = (event: MessageEvent) => {
        let data: any;
        try {
          data = JSON.parse(event.data as string);
        } catch {
          return;
        }

        if (data.type !== 'chat') return;
        if (data.wss_close === true) return; // ← ignore the close confirmation frame

        this.ngZone.run(() => {
          //if (!this.isStreaming) {
          if (!this.assistantStarted) {
            // First chunk — remove typing indicator, start new assistant message
            this.showTyping = false;
            this.isStreaming = true;
            this.assistantStarted = true;
            /* this.messages.push({
              role: 'assistant',
              content: data.content || '',
              time: this.formatTime(),
            }); */
            this.messages = [
              ...this.messages,
              {
                role: 'assistant',
                content: data.content || '',
                time: this.formatTime(),
              }
            ];
          } else {
            // Subsequent chunks — append to last message
           /*  const last = this.messages[this.messages.length - 1];
            //if (last.role === 'assistant') {
            if (last.role === 'assistant' && data.content) { // ← only append if content is non-null
              last.content += data.content || '';
            } */
           if (data.content) {
              const updated = [...this.messages];
              const last = { ...updated[updated.length - 1] };
              last.content += data.content;
              updated[updated.length - 1] = last;
              this.messages = updated; // ← new array reference triggers ngFor to re-render
            }
          }

          this.shouldScrollBottom = true;

          if (data.end === true) {
            const fullContent =
              this.messages[this.messages.length - 1].content;
            this.wsHistory.push({
              role: 'assistant',
              stream_id: this.currentStreamId || '',
              content: fullContent,
              end: true,
              created_at: new Date().toISOString(),
            });
            this.currentStreamId = null;
            this.isStreaming = false;
            this.loadingService.isLoading(false);
            this.cdr.detectChanges();
            //setTimeout(() => this.chatInput.nativeElement.focus());
            setTimeout(() => {
              if (this.chatInput) {
                this.chatInput.nativeElement.focus();
              }
            });

            this.assistantStarted = false;
          }

          this.cdr.detectChanges();

          this.loadingService.isLoading(false);
        });
      };
    });
  }

  // ── Send ──────────────────────────────────────────────────────────────────
  sendMessage(): void {
    const text = this.inputText.trim();
    if (
      !text ||
      !this.ws ||
      this.ws.readyState !== WebSocket.OPEN ||
      this.isStreaming
    ) return;

    this.inputText = '';
    this.isStreaming = true;
    this.assistantStarted = false;

    const streamId = this.uid(11);
    this.currentStreamId = streamId;
    const now = new Date().toISOString();

    this.messages.push({ role: 'user', content: text, time: this.formatTime() });

    this.wsHistory.push({ role: 'user', stream_id: streamId, content: text, end: true, created_at: now });
    this.wsHistory.push({ role: 'assistant', stream_id: streamId, content: '', end: false, created_at: now });

    this.showTyping = true;
    this.shouldScrollBottom = true;
    this.cdr.detectChanges();

    const payload = {
      type: 'chat',
      stream_id: streamId,
      session_uid: this.sessionUid,
      dialog_id: null,
      content: text,
      bot_id: this.BOT_ID,
      history: [...this.wsHistory],
      is_allow_bot_leads: true,
      is_allow_bot_leads_custom_fields: true,
      domain: this.DOMAIN,
      wss_id: '_' + this.uid(10),
    };

    this.ws.send(JSON.stringify(payload));
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  onAvatarError(): void {
    this.avatarError = true;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  private scrollBottom(): void {
    const el = this.messagesContainer.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  private formatTime(): string {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private uid(len: number): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    return Array.from(
      { length: len },
      () => chars[Math.floor(Math.random() * chars.length)]
    ).join('');
  }

  get isSendDisabled(): boolean {
    return !this.inputText.trim() || !this.isConnected || this.isStreaming;
  }

 /*  onIframeFocusLost(): void {
    if (document.activeElement.tagName === 'IFRAME') {
      // User clicked into the iframe — keep it expanded
      console.log('in IFRAME');
      this.iframeHeight = '350px';
      //return;
    } else {
      // User clicked outside the iframe — collapse it
      console.log('outside IFRAME');
      this.iframeHeight = '40px';
      //return;
    }
    // Focus left the window entirely (e.g. alt-tab), optionally collapse
    // this.collapseChatBot();
  } */
}